import { motion } from "framer-motion";
import { Markdown } from "../markdown";
import type { OcrResultData } from "../types";

/**
 * Beyond Sure — OCR / document-read result card.
 *
 * Use case: user uploaded a policy PDF; the OCR pipeline returns
 * structured highlights + a freeform summary. Rendered inline as
 * "I read your policy — here's what I found."
 *
 * THIS COMPONENT IS REUSABLE. The standalone OCR page (separate
 * surface, later phase) will mount the same component with its
 * own `data` shape — keep the props minimal and don't pull in any
 * chat-specific context.
 *
 * Phase 3 wires real uploads (`/api/chat/upload`); for now the chat
 * surface attaches mock data on file-attach so the user sees the
 * full pattern end-to-end.
 */

interface OcrResultCardProps {
  data: OcrResultData;
}

export function OcrResultCard({ data }: OcrResultCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      data-testid="message-ocr-result"
      className={[
        "rounded-[14px] overflow-hidden",
        "bg-[rgb(255_255_255_/_0.92)] backdrop-blur-sm",
        "border border-[var(--color-ink-08)]",
        "shadow-[0_6px_22px_-14px_rgba(20,75,51,0.28)]",
      ].join(" ")}
    >
      {/* Doc strip — shows filename so user knows which doc this is for */}
      <div
        className={[
          "flex items-center gap-2.5 px-4 sm:px-5 py-2.5",
          "border-b border-[var(--color-ink-08)]",
          "bg-[var(--color-paper)]",
        ].join(" ")}
      >
        <span
          className="inline-flex items-center justify-center h-7 w-7 rounded-[8px] shrink-0"
          style={{ backgroundColor: "var(--color-brand-soft)" }}
          aria-hidden
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M7 3h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
              stroke="var(--color-brand-deep)"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
            <path
              d="M14 3v5h5"
              stroke="var(--color-brand-deep)"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] bsure-caps font-semibold tracking-[0.16em] text-[var(--color-brand-deep)]">
            I read your policy
          </p>
          <p className="text-[13px] font-medium text-[var(--color-ink)] truncate">
            {data.fileName}
          </p>
        </div>
      </div>

      <div className="px-4 sm:px-5 py-4">
        <h3 className="bsure-display text-[17px] sm:text-[19px] font-bold text-[var(--color-ink)] leading-[1.25]">
          {data.policyName}
        </h3>

        {/* Highlights grid — most-important fields, compact */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {data.highlights.map((h, i) => (
            <div
              key={`${h.label}-${i}`}
              className={[
                "rounded-[10px] px-3 py-2.5",
                "bg-[var(--color-brand-soft)] text-[var(--color-ink)]",
              ].join(" ")}
            >
              <p className="bsure-caps text-[9.5px] font-semibold tracking-[0.14em] text-[var(--color-brand-deep)]">
                {h.label}
              </p>
              <p className="mt-0.5 text-[13.5px] font-medium">{h.value}</p>
            </div>
          ))}
        </div>

        {/* Free-form summary, markdown allowed */}
        {data.summary ? (
          <div className="mt-4 text-[14px] leading-[1.6] text-[var(--color-ink)]">
            <Markdown source={data.summary} />
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}

export default OcrResultCard;
